import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Heart, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatPrice, resolveImage } from "@/lib/api";
import Stars from "@/components/Stars";
import { useProductRatings } from "@/hooks/useProductRatings";

export default function ProductCard({ product }) {
  const rating = useProductRatings()[product.product_id];
  const { addItem } = useCart();
  const outOfStock = !product.bookable && (product.stock ?? 1) <= 0;

  const handleAdd = () => {
    addItem({ ...product, image_url: resolveImage(product.image_url) });
    toast.success(`${product.name} ajouté au panier`);
  };

  return (
    <article
      data-testid={`product-card-${product.product_id}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-amber-800/15 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <Link
        to={`/produit/${product.product_id}`}
        data-testid={`product-link-${product.product_id}`}
        className="relative block aspect-[4/3] overflow-hidden bg-white"
      >
        <img
          src={resolveImage(product.image_url)}
          alt={product.name}
          loading="lazy"
          className={`h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105 ${outOfStock ? "opacity-60 grayscale" : ""}`}
        />
        {outOfStock ? (
          <span
            data-testid={`product-soldout-${product.product_id}`}
            className="absolute left-4 top-4 rounded-full bg-beeswax/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-cream backdrop-blur"
          >
            Épuisé
          </span>
        ) : (
          product.badge && (
            <span
              data-testid={`product-badge-${product.product_id}`}
              className="absolute left-4 top-4 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-900/80 backdrop-blur"
            >
              {product.badge}
            </span>
          )
        )}
        {rating?.top && (
          <span data-testid={`product-top-rated-${product.product_id}`} className="absolute bottom-3 left-4 flex items-center gap-1 rounded-full bg-amber-deep px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white shadow-sm">
            <Award size={12} /> Coup de cœur clients
          </span>
        )}
        {product.featured && (
          <motion.span
            data-testid={`product-coupdecoeur-${product.product_id}`}
            className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-terra px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-cream shadow-sm"
            animate={{ scale: [1, 1.07, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.span
              animate={{ scale: [1, 1.25, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="flex"
            >
              <Heart size={11} className="fill-cream" />
            </motion.span>
            Coup de cœur
          </motion.span>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-6">
        <Link to={`/produit/${product.product_id}`} className="line-clamp-2 min-h-[3.5rem] font-serif text-xl font-semibold leading-snug text-bark transition-colors hover:text-amber-deep">
          {product.name}
        </Link>
        <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">{product.weight}</p>
        {rating && (
          <p data-testid={`product-card-rating-${product.product_id}`} className="-mt-1 flex items-center gap-1.5 text-xs text-bark-soft">
            <Stars value={rating.avg} size={13} /> {rating.avg.toLocaleString("fr-FR")} ({rating.count})
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-2">
          <span data-testid={`product-price-${product.product_id}`} className="font-serif text-2xl font-semibold text-amber-deep">
            {formatPrice(product.price_cents)}
          </span>
          {product.bookable ? (
            <span
              data-testid={`book-${product.product_id}`}
              className="flex items-center gap-2 rounded-full border-2 border-amber-deep px-4 py-2.5 text-sm font-medium text-amber-deep transition-all duration-200 hover:bg-amber-deep hover:text-white active:scale-95"
            >
              Réserver
            </span>
          ) : (
            <button
              data-testid={`add-to-cart-${product.product_id}`}
              onClick={handleAdd}
              disabled={outOfStock}
              className="flex items-center gap-2 rounded-full bg-amber-deep px-4 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/10 transition-all duration-200 hover:bg-honey active:scale-95 disabled:cursor-not-allowed disabled:bg-stone-mute/40 disabled:shadow-none"
            >
              <ShoppingBag size={16} />
              {outOfStock ? "Épuisé" : "Ajouter"}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
